import { EmailAddress, PhoneNumber } from '@shared/types';
import { Group, Stack, Text, TextInput, Title, TitleSize } from '@mantine/core';
import { useEffect, useState } from 'react';
import { HumanContact } from '../../types';
import styles from './HumanContactForm.module.css';
import { PhoneNumberFields } from './PhoneNumberFields';
import { EmailAddressFields } from './EmailAddressFields';
import { AtSignIcon, PhoneIcon, User2Icon } from 'lucide-react';
import { CollapsibleContactCard } from './CollapsibleContactCard';
import PractistSelect from '@/components/PractistSelect/PractistSelect';
import { SelectOption } from '@/components/types';

// Common language options, defaulting to English
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', native: '' },
  { value: 'es', label: 'Spanish', native: 'Español' },
  { value: 'ar', label: 'Arabic', native: 'العربية' },
  { value: 'zh', label: 'Chinese', native: '中文' },
  { value: 'fr', label: 'French', native: 'Français' },
  { value: 'de', label: 'German', native: 'Deutsch' },
  { value: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { value: 'it', label: 'Italian', native: 'Italiano' },
  { value: 'ja', label: 'Japanese', native: '日本語' },
  { value: 'ko', label: 'Korean', native: '한국어' },
  { value: 'pt', label: 'Portuguese', native: 'Português' },
  { value: 'ru', label: 'Russian', native: 'Русский' },
  { value: 'so', label: 'Somali', native: 'Soomaali' },
  { value: 'vi', label: 'Vietnamese', native: 'Tiếng Việt' },
];

const renderLanguageOption = (option: SelectOption, isActive: boolean) => {
  const languageOption = LANGUAGE_OPTIONS.find(
    (lang) => lang.value === option.value,
  );
  if (!languageOption) return option.label;

  return (
    <span>
      {languageOption.label}{' '}
      {languageOption.native && (
        <span
          style={{ color: isActive ? 'white' : 'var(--mantine-color-gray-5)' }}
        >
          {languageOption.native}
        </span>
      )}
    </span>
  );
};

const HumanContactForm = ({
  title,
  human,
  canBePrimary,
  isPrimary,
  titleSize,
  onChange,
  onWantsPrimary,
  onDelete,
}: {
  title: string;
  human: HumanContact;
  canBePrimary: boolean;
  isPrimary: boolean;
  titleSize?: TitleSize;
  onChange: (human: HumanContact) => void;
  onWantsPrimary: () => void;
  onDelete: () => void;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(human.firestoreId !== null);

  return (
    <CollapsibleContactCard
      icon={<User2Icon size={16} strokeWidth={2} />}
      title={<HumanTitle human={human} />}
      accessibilityLabel={labelForHuman(human)}
      canDelete={true}
      onDelete={onDelete}
      canMakePrimary={canBePrimary}
      isPrimary={isPrimary}
      onWantsPrimary={onWantsPrimary}
      childrenWhenCollapsed={<CollapsedHumanInfo human={human} />}
      childrenWhenExpanded={
        <ExpandedHumanInfo human={human} onChange={onChange} />
      }
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
    />
  );
};

export default HumanContactForm;

const labelForHuman = (human: HumanContact) => {
  const parts = [];
  if (human.firstName !== '') {
    parts.push(human.firstName);
  }
  if (human.lastName !== '') {
    parts.push(human.lastName);
  }
  if (parts.length === 0) {
    parts.push('Unnamed person');
  }
  return parts.join(' ');
};

const HumanTitle = ({ human }: { human: HumanContact }) => {
  return (
    <Title
      order={3}
      size='h4'
      style={{ margin: '0', color: 'var(--mantine-color-text)' }}
    >
      {labelForHuman(human)}
    </Title>
  );
};

const ExpandedHumanInfo = ({
  human,
  onChange,
}: {
  human: HumanContact;
  onChange: (human: HumanContact) => void;
}) => {
  const [firstName, setFirstName] = useState(human.firstName);
  const [lastName, setLastName] = useState(human.lastName);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>(
    human.phoneNumbers ?? [],
  );
  const [emailAddresses, setEmailAddresses] = useState<EmailAddress[]>(
    human.emailAddresses ?? [],
  );
  const [primaryPhoneNumberId, setPrimaryPhoneNumberId] = useState<string>(
    human.primaryPhoneNumberId ?? '',
  );
  const [primaryEmailAddressId, setPrimaryEmailAddressId] = useState<string>(
    human.primaryEmailAddressId ?? '',
  );
  const [preferredLanguage, setPreferredLanguage] = useState<string>(
    human.preferredLanguage ?? 'en',
  );

  useEffect(() => {
    onChange({
      tempId: human.tempId,
      firestoreId: human.firestoreId,
      role: human.role,
      firstName,
      lastName,
      phoneNumbers,
      emailAddresses,
      primaryPhoneNumberId,
      primaryEmailAddressId,
      preferredLanguage,
      type: 'human',
    });
  }, [
    firstName,
    lastName,
    phoneNumbers,
    emailAddresses,
    primaryPhoneNumberId,
    primaryEmailAddressId,
    preferredLanguage,
  ]);
  return (
    <Stack gap='lg' px='lg' pt='0' pb='md'>
      <div className={styles.contactNameSection}>
        <TextInput
          label='First Name'
          key={`${human.tempId}-firstName`}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          autoComplete='off'
          flex={'1 1 auto'}
        />
        <TextInput
          label='Last Name'
          key={`${human.tempId}-lastName`}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          autoComplete='off'
          flex={'1 1 auto'}
        />
      </div>
      <PhoneNumberFields
        data={{
          items: phoneNumbers,
          primaryItemId: primaryPhoneNumberId,
        }}
        onChange={(data) => {
          setPhoneNumbers(data.items);
          setPrimaryPhoneNumberId(data.primaryItemId ?? '');
        }}
      />
      <EmailAddressFields
        data={{
          items: emailAddresses,
          primaryItemId: primaryEmailAddressId,
        }}
        onChange={(data) => {
          setEmailAddresses(data.items);
          setPrimaryEmailAddressId(data.primaryItemId ?? '');
        }}
      />
      <PractistSelect
        label='Preferred Language'
        value={preferredLanguage}
        onOptionSelected={(value) => setPreferredLanguage(value || 'en')}
        data={LANGUAGE_OPTIONS}
        renderOption={renderLanguageOption}
      />
    </Stack>
  );
};

const CollapsedHumanInfo = ({ human }: { human: HumanContact }) => {
  return human.phoneNumbers.length === 0 &&
    human.emailAddresses.length === 0 ? (
    <Text pb='xs' pt='xs' size='sm' c='dimmed'>
      No contact information
    </Text>
  ) : (
    <Stack gap='0.25rem'>
      {human.phoneNumbers.length > 0 && (
        <Group gap='xs'>
          <PhoneIcon size={14} color='var(--mantine-color-dimmed)' />
          <Text size='sm' c='dimmed'>
            {human.phoneNumbers.map((phone) => phone.value).join(', ')}
          </Text>
        </Group>
      )}
      {human.emailAddresses.length > 0 && (
        <Group gap='xs'>
          <AtSignIcon size={14} color='var(--mantine-color-dimmed)' />
          <Text size='sm' c='dimmed'>
            {human.emailAddresses.map((email) => email.value).join(', ')}
          </Text>
        </Group>
      )}
    </Stack>
  );
};
